/** Data for the San Francisco congestion-pricing pages. Everything is pre-computed:
 *    /api/traffic/workshop  scenario definitions, policy metrics, the charging zone
 *    /api/traffic/links     a stored run's per-edge V/C around the zone, or on given edges
 *    /api/traffic/compare   baseline against one stored scenario
 *    /api/traffic/od-flows  trips into the zone, origin to destination, per window
 *    /api/traffic/od-all    every car trip from one origin TAZ, per window
 *  workshop.json is mock data until the scenario runs land; its shape is the
 *  contract the real data must keep. */

import { fetchPrebakedComparison, fetchEdgeTraffic } from '../../utils/trafficDataset';
import { bandOutlines, treatedEdgeIds } from '../../utils/corridors';
import { routeCostId } from './figures';
import { visibleScenarios } from '../../config/features';

export async function fetchWorkshop(cbsa) {
  const res = await fetch(`/api/traffic/workshop?cbsa=${encodeURIComponent(cbsa)}`);
  if (res.status === 404) {
    throw new Error(`This metro has no workshop.json (backend/fixtures/prebaked/${cbsa}/workshop.json).`);
  }
  if (!res.ok) throw new Error(`workshop ${res.status}`);
  const workshop = await res.json();
  workshop.scenarios = visibleScenarios(workshop.scenarios);
  return workshop;
}

/** The zone's polygons, each [outer ring, ...holes]: one for a Polygon, one per
 *  part for a MultiPolygon, none for anything else. */
export function zonePolygons(geometry) {
  if (geometry?.type === 'Polygon') return [geometry.coordinates];
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates;
  return [];
}

/** The zone's bounding box widened by `margin` degrees, as a closed ring: the
 *  roads the map draws, inside the cordon and around its edge. */
export function zoneBox(geometry, margin = 0.025) {
  const points = zonePolygons(geometry).flatMap((polygon) => polygon[0]);
  if (!points.length) throw new Error('the charging zone is not a polygon');
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const [w, s, e, n] = [Math.min(...xs) - margin, Math.min(...ys) - margin,
                        Math.max(...xs) + margin, Math.max(...ys) + margin];
  return [[w, s], [e, s], [e, n], [w, n], [w, s]];
}

const MAJOR = ['motorway', 'trunk', 'primary'];
const LINKS = ['motorway_link', 'trunk_link', 'primary_link'];
const SECONDARY = ['secondary', 'secondary_link'];
const TERTIARY = ['tertiary', 'tertiary_link'];

/** The road classes a map in view draws at a zoom, from the most general down: every road
 *  of those classes in view, so no zoom draws part of a class. In San Francisco, on a
 *  1500 × 800 px map at each tier's lowest zoom, a tier is at most 21,400 segments; the
 *  major roads of the whole region are 35,000. `what` describes the tier, null for every road. */
const ROAD_TIERS = [
  { id: 'all', minZoom: 12.5, highways: null, what: null },
  { id: 'tertiary', minZoom: 11.5, highways: [...MAJOR, ...LINKS, ...SECONDARY, ...TERTIARY], what: 'freeways, arterials and collectors' },
  { id: 'secondary', minZoom: 10.5, highways: [...MAJOR, ...LINKS, ...SECONDARY], what: 'freeways and arterials' },
  { id: 'links', minZoom: 9.5, highways: [...MAJOR, ...LINKS], what: 'freeways and major arterials' },
  { id: 'major', minZoom: -Infinity, highways: MAJOR, what: 'freeways and major arterials' },
];
export const roadTier = (zoom) => ROAD_TIERS.find((t) => zoom >= t.minZoom);
// /links' ceiling; no tier comes near it, so a view is never cut short.
const VIEW_LIMIT = 50000;

async function fetchRunRoads({ cbsa, runId, scenario, ring, highways, hour, limit, signal }) {
  const res = await fetch('/api/traffic/links', {
    method: 'POST',
    ...(signal ? { signal } : {}),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cbsa, runId, scenario, polygon: ring,
      ...(highways ? { highways } : {}),
      ...(limit ? { limit } : {}),
      ...(hour !== null && hour !== undefined ? { hour } : {}),
    }),
  });
  if (!res.ok) throw new Error(`traffic/links ${res.status}`);
  return res.json();
}

// One request per (mode, run) for the life of the page: both pages, and every
// thumbnail on the comparison page, draw from the same few runs.
const roadCache = new Map();

/** `mode` 'run': one stored run's roads around the zone, V/C per edge.
 *  `mode` 'difference': baseline against `run`, every edge whose V/C moved by 0.02 or
 *  more, network-wide. */
export function getRoads(workshop, run, mode, hour = null) {
  const area = mode === 'difference' ? 'all' : 'zone';
  // The zone comes from the pack's pricing section, which a pack may not have yet.
  if (area === 'zone' && !workshop.zone?.geometry) {
    return Promise.reject(new Error("this metro's pre-computed pack has no charging zone"));
  }
  const key = `${mode}:${area}:${workshop.run_id}:${run}:${hour ?? ''}`;
  let p = roadCache.get(key);
  if (!p) {
    const args = { cbsa: workshop.cbsa_id, runId: workshop.run_id, scenario: run, hour };
    p = mode === 'difference'
      ? fetchPrebakedComparison(args.cbsa, args.runId, run, { hour: hour ?? workshop.default_hour })
      : fetchRunRoads({ ...args, ring: zoneBox(workshop.zone.geometry) });
    p.catch(() => roadCache.delete(key));
    roadCache.set(key, p);
  }
  return p;
}

/** One run's roads in the part of the map in view (`view`: { bounds: [w, s, e, n], zoom }),
 *  the classes of its zoom's tier. Not cached: a view is seldom asked for twice. */
export function getViewRoads(workshop, run, hour, view, signal = null) {
  const [w, s, e, n] = view.bounds;
  return fetchRunRoads({
    signal,
    cbsa: workshop.cbsa_id, runId: workshop.run_id, scenario: run, hour,
    ring: [[w, s], [e, s], [e, n], [w, n], [w, s]],
    highways: roadTier(view.zoom).highways,
    limit: VIEW_LIMIT,
  });
}

const bandCache = new Map();

/** The route-cost scenario's protected neighbourhoods, as the main map outlines them
 *  ([{ ring }], see bandOutlines); [] when the workshop has no such scenario. Read once
 *  per run: the band streets' shape is the same in every run and hour, so it comes from
 *  the baseline at the default hour. */
export function getBandOutlines(workshop) {
  const id = routeCostId(workshop);
  const corridors = id ? workshop.model.scenarios[id].routeCost.corridors : null;
  if (!corridors) return Promise.resolve([]);
  const key = `${workshop.cbsa_id}:${workshop.run_id}:${id}`;
  let p = bandCache.get(key);
  if (!p) {
    p = fetchEdgeTraffic(workshop.cbsa_id, workshop.run_id, 'baseline', workshop.default_hour ?? null, treatedEdgeIds(corridors))
      .then((r) => bandOutlines(corridors,
        Object.fromEntries(r.features.map((f) => [f.properties.edgeId, f.geometry.coordinates]))));
    p.catch(() => bandCache.delete(key));
    bandCache.set(key, p);
  }
  return p;
}

const flowCache = new Map();

/** Trips into the zone for one run, summed over `hours`. A missing file comes back as
 *  the server's message. */
export function getFlows(workshop, run, hours) {
  const key = `${workshop.cbsa_id}:${workshop.run_id}:${run}:${hours.join(',')}`;
  let p = flowCache.get(key);
  if (!p) {
    const q = new URLSearchParams({ cbsa: workshop.cbsa_id, scenario: run, hours: hours.join(',') });
    p = fetch(`/api/traffic/od-flows?${q}`).then(async (res) => {
      if (res.ok) return res.json();
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `od-flows ${res.status}`);
    });
    p.catch(() => flowCache.delete(key));
    flowCache.set(key, p);
  }
  return p;
}

// Bounded: one entry per (origin, window) clicked.
const odAllCache = new Map();
const OD_ALL_KEEP = 64;

/** Every car trip from the TAZ `origin` in the metro's run, summed over `hours`: rows of
 *  [destination TAZ, trips], heaviest first. */
export function getOdAll(workshop, origin, hours) {
  const key = `${workshop.cbsa_id}:${origin}:${hours.join(',')}`;
  let p = odAllCache.get(key);
  if (!p) {
    const q = new URLSearchParams({ cbsa: workshop.cbsa_id, origin, hours: hours.join(',') });
    p = fetch(`/api/traffic/od-all?${q}`).then(async (res) => {
      if (res.ok) return res.json();
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `od-all ${res.status}`);
    });
    p.catch(() => odAllCache.delete(key));
    odAllCache.set(key, p);
    if (odAllCache.size > OD_ALL_KEEP) odAllCache.delete(odAllCache.keys().next().value);
  }
  return p;
}
